import React from 'react';
import { SearchIcon, BellIcon, ChevronDownIcon, HomeIcon, GaugeIcon, BarChart3Icon, WrenchIcon, FileTextIcon, LayoutGridIcon, DropletIcon, AlertTriangleIcon, UsersIcon, LeafIcon } from 'lucide-react';
import { waterDashboard as d, type Bi } from '../../data/waterHero';
type P = {
  isFR: boolean;
};
// Exact logo files used in the site header (components/Header.tsx)
const BCITI_LOGO = 'https://cdn.magicpatterns.com/uploads/kFQruRR3oauw7NNUdgFajn/Logo_BCITI_(5).png';
const ATNA_LOGO = 'https://cdn.magicpatterns.com/uploads/d6cqpG2SGUofNqC1hEedXv/Logo_ATNA_(5).png';
const NAV_ICONS = {
  overview: LayoutGridIcon,
  properties: HomeIcon,
  meters: GaugeIcon,
  consumption: BarChart3Icon,
  alerts: BellIcon,
  interventions: WrenchIcon,
  reports: FileTextIcon
} as const;
const KPI_ICON = {
  meters: {
    Icon: DropletIcon,
    bg: '#EEF0FE',
    fg: '#4F46E5'
  },
  leaks: {
    Icon: AlertTriangleIcon,
    bg: '#FDECEC',
    fg: '#E5484D'
  },
  linked: {
    Icon: UsersIcon,
    bg: '#EEF0FE',
    fg: '#4F46E5'
  },
  down: {
    Icon: LeafIcon,
    bg: '#E8F6EF',
    fg: '#1F9D7A'
  }
} as const;
const PANEL = 'rounded-xl border border-[#EEEEF4] bg-white';
function DailyBars({
  isFR
}: P) {
  const days = isFR ? d.days.fr : d.days.en;
  const max = 400;
  return <div className="flex gap-2" data-id="element-7606" data-mp="1d96lmjigpz17">
      <div className="flex flex-col justify-between h-[96px] font-sans text-[8.5px] text-[#9AA0B2] pb-4" aria-hidden="true" data-id="element-7607" data-mp="18b4tl9326pdl">
        {[400, 300, 200, 100, 0].map(v => <span key={v} data-id="element-7608" data-mp="kj6himvscpze">{v}</span>)}
      </div>
      <ul className="flex-1 flex items-end justify-between gap-1.5 h-[96px]" data-id="element-7609" data-mp="fn900d8sddl1">
        {d.daily.map((v, i) => <li key={days[i]} className="flex-1 flex flex-col items-center gap-1" data-id="element-7610" data-mp="ubvlyxsx53l8" data-mpi={i}>
            <span className="w-full max-w-[20px] rounded-t-[3px]" style={{
          height: `${v / max * 80}px`,
          background: i === d.daily.length - 1 ? '#4F46E5' : '#DCDDFB'
        }} aria-hidden="true" data-id="element-7611" data-mp="pjccabxcqslp" />
            <span className="font-sans text-[8.5px] text-[#8A90A2]" data-id="element-7612" data-mp="3syzw2w6k3rdt">{days[i]}</span>
          </li>)}
      </ul>
    </div>;
}
function SectorDonut({
  isFR
}: P) {
  const pick = (v: Bi) => isFR ? v.fr : v.en;
  const r = 36;
  const circ = 2 * Math.PI * r;
  let offset = 0;
  return <div className="flex items-center gap-4" data-id="element-7613" data-mp="vcxh5spnpd2q">
      <div className="relative w-[96px] h-[96px] shrink-0" data-id="element-7614" data-mp="10brzyunmj1mu">
        <svg viewBox="0 0 100 100" className="w-full h-full -rotate-90" aria-hidden="true" data-id="element-7615" data-mp="10dcyswgz16qi">
          {d.sectors.map(s => {
          const len = s.pct / 100 * circ;
          const el = <circle key={s.key} cx="50" cy="50" r={r} fill="none" stroke={s.color} strokeWidth="11" strokeDasharray={`${len} ${circ - len}`} strokeDashoffset={-offset} data-id="element-7616" data-mp="7muu5r0lmxaz" />;
          offset += len;
          return el;
        })}
        </svg>
        <span className="absolute inset-0 flex flex-col items-center justify-center" data-id="element-7617" data-mp="1o132ohojb63m">
          <span className="font-sans text-[13px] font-semibold text-[#121A3A] leading-none" data-id="element-7618" data-mp="1szxlhjmi4unq">{isFR ? '12 842' : '12,842'}</span>
          <span className="font-sans text-[8.5px] text-[#8A90A2] mt-0.5" data-id="element-7619" data-mp="3di86zivgywsl">{pick(d.donutCenter)}</span>
        </span>
      </div>
      <ul className="space-y-1.5" data-id="element-7620" data-mp="38jdpck98nea2">
        {d.sectors.map(s => <li key={s.key} className="flex items-center gap-2 font-sans text-[10.5px] text-[#3A4258] whitespace-nowrap" data-id="element-7621" data-mp="33rdj6a2krp51">
            <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{
          background: s.color
        }} aria-hidden="true" data-id="element-7622" data-mp="2ysap1ko4v3bu" />
            <span className="font-semibold w-8" data-id="element-7623" data-mp="2uss2ylkksm7f">{s.pct}{isFR ? ' %' : '%'}</span>
            <span data-id="element-7624" data-mp="2prkwcpu65t2c">{pick(s.label)}</span>
          </li>)}
      </ul>
    </div>;
}
export function WaterLaptop({
  isFR
}: P) {
  const pick = (v: Bi) => isFR ? v.fr : v.en;
  return <div data-id="element-7625" data-mp="2fv9gjpvdel35">
      <div className="rounded-t-[22px] bg-[#1B1D26] p-[9px] pb-[12px] shadow-[0_40px_70px_-40px_rgba(20,20,60,0.55)]" data-id="element-7626" data-mp="2fvhr3b40teb6">
        <div className="rounded-[14px] bg-[#FCFCFE] overflow-hidden" data-id="element-7627" data-mp="iztubds51vn1">
          <div className="flex" data-id="element-7628" data-mp="e44s12oqi0n1">
            {/* Sidebar */}
            <div className="hidden sm:flex w-[136px] shrink-0 flex-col bg-white border-r border-[#F2F2F7]" data-id="element-7629" data-mp="m3smq2rai3ho">
              <div className="flex items-center gap-1.5 px-3 h-[52px] border-b border-[#F2F2F7]" data-id="element-7630" data-mp="r35hrasbegvc">
                <img src={BCITI_LOGO} alt="BCITI" className="h-[18px] w-auto object-contain" data-id="element-7631" data-mp="c5v0fkszqgrp" />
                <span className="text-[#121212]/25 font-sans text-xs font-light" aria-hidden="true" data-id="element-7632" data-mp="h3wsuwcb3sfu">|</span>
                <img src={ATNA_LOGO} alt="ATNA" className="h-[14px] w-auto object-contain" data-id="element-7633" data-mp="3croxi1wodzp" />
              </div>
              <ul className="p-2 space-y-0.5" data-id="element-7634" data-mp="84t87nxhr4z8">
                {d.nav.map(n => {
                const Icon = NAV_ICONS[n.key];
                const active = n.key === 'overview';
                return <li key={n.key} className={`flex items-center gap-2 rounded-lg px-2.5 py-2 font-sans text-[11px] whitespace-nowrap ${active ? 'bg-[#EEEDFD] text-[#4F46E5] font-semibold' : 'text-[#3A4258]'}`} data-id="element-7635" data-mp="3ujufq8du0i9e">
                      <Icon className={`w-3.5 h-3.5 shrink-0 ${active ? 'text-[#4F46E5]' : 'text-[#8A90A2]'}`} strokeWidth={1.8} aria-hidden="true" data-id="element-7636" data-mp="31yyutpvthxk8" />
                      {pick(n.label)}
                    </li>;
              })}
              </ul>
            </div>
            <div className="flex-1 min-w-0" data-id="element-7637" data-mp="35x4u6cubtav4">
              {/* Top bar */}
              <div className="flex items-center gap-3 px-4 h-[52px] bg-white border-b border-[#F2F2F7]" data-id="element-7638" data-mp="3kufgkh8ic15m">
                <div className="flex-1 min-w-0 flex items-center gap-2 rounded-lg bg-[#F5F6FA] px-3 py-1.5" data-id="element-7639" data-mp="3fvufkflfldg4">
                  <SearchIcon className="w-3 h-3 text-[#9AA0B2] shrink-0" aria-hidden="true" data-id="element-7640" data-mp="3tnin05kqjdqr" />
                  <span className="font-sans text-[10px] text-[#9AA0B2] truncate" data-id="element-7641" data-mp="3pmys78nmwm7s">{pick(d.search)}</span>
                </div>
                <span className="hidden md:flex items-center gap-1.5 rounded-lg border border-[#EEEEF4] px-2.5 py-1.5 font-sans text-[10px] text-[#3A4258] whitespace-nowrap" data-id="element-7642" data-mp="7ehhm6tpjkvp">
                  {pick(d.territory)}
                  <ChevronDownIcon className="w-3 h-3" aria-hidden="true" data-id="element-7643" data-mp="30bandhg8602t" />
                </span>
                <span className="relative" aria-hidden="true" data-id="element-7644" data-mp="35adtl57xyccd">
                  <BellIcon className="w-3.5 h-3.5 text-[#5B6478]" data-id="element-7645" data-mp="3592o90yukwj5" />
                  <span className="absolute -top-0.5 -right-0.5 w-1.5 h-1.5 rounded-full bg-[#E5484D]" data-id="element-7646" data-mp="3a25kjgv0ek31" />
                </span>
                <span className="w-7 h-7 rounded-full bg-[#4F46E5] flex items-center justify-center font-sans text-[9.5px] font-semibold text-white shrink-0" aria-hidden="true" data-id="element-7647" data-mp="3ezp0qmb9p6xm">
                  LG
                </span>
              </div>
              <div className="p-4" data-id="element-7648" data-mp="3szpy3rsbpw8c">
                <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5" data-id="element-7649" data-mp="3sywwt6c4vyxl">
                  <p className="font-sans text-[16px] font-semibold text-[#121A3A] mr-auto" data-id="element-7650" data-mp="1tnni4riwrjzf">{pick(d.title)}</p>
                  <span className="flex items-center gap-1.5 font-sans text-[9.5px] text-[#3A4258] whitespace-nowrap" data-id="element-7651" data-mp="1yn04lb5vap1h">
                    <span className="w-1.5 h-1.5 rounded-full bg-[#1F9D7A]" aria-hidden="true" data-id="element-7652" data-mp="1r40lz7q8d1j6" />
                    {pick(d.live)}
                  </span>
                  <span className="flex items-center gap-1 rounded-md border border-[#EEEEF4] px-2 py-1 font-sans text-[9.5px] text-[#3A4258] whitespace-nowrap" data-id="element-7653" data-mp="1hc38tvfnvzht">
                    {pick(d.period)}
                    <ChevronDownIcon className="w-3 h-3" aria-hidden="true" data-id="element-7654" data-mp="2a1towa0utbgk" />
                  </span>
                </div>
                <ul className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-2" data-id="element-7655" data-mp="2538pudxc8xtp">
                  {d.kpis.map(k => {
                  const {
                    Icon,
                    bg,
                    fg
                  } = KPI_ICON[k.key];
                  return <li key={k.key} className={`${PANEL} p-2.5 flex items-start gap-2`} data-id="element-7656" data-mp="1w0r19su7nt3j">
                        <span className="w-8 h-8 rounded-full flex items-center justify-center shrink-0" style={{
                      background: bg
                    }} aria-hidden="true" data-id="element-7657" data-mp="op0fritr8gyy">
                          <Icon className="w-4 h-4" style={{
                        color: fg
                      }} strokeWidth={1.9} data-id="element-7658" data-mp="2jr3r36c05wqb" />
                        </span>
                        <span className="min-w-0" data-id="element-7659" data-mp="2oqq7tpr1hv3v">
                          <span className="block font-sans text-[17px] font-semibold text-[#121A3A] leading-none" data-id="element-7660" data-mp="2oo3xjicrl1hy">{pick(k.value)}</span>
                          <span className="block font-sans text-[9.5px] text-[#5B6478] mt-1 leading-tight" data-id="element-7661" data-mp="2fp8uug8xi9je">{pick(k.label)}</span>
                          {k.trend && <span className={`block font-sans text-[9px] font-semibold mt-1 ${k.tone === 'bad' ? 'text-[#E5484D]' : 'text-[#1F9D7A]'}`} data-id="element-7662" data-mp="33hgwioxc4e2r">{pick(k.trend)}</span>}
                        </span>
                      </li>;
                })}
                </ul>
                <div className="mt-2 grid grid-cols-1 md:grid-cols-2 gap-2" data-id="element-7663" data-mp="3mdytpluyd66e">
                  <div className={`${PANEL} p-3`} data-id="element-7664" data-mp="3mbvxul74citr">
                    <p className="font-sans text-[11px] font-semibold text-[#121A3A] mb-2" data-id="element-7665" data-mp="3r5qpqbwvo43x">{pick(d.dailyTitle)}</p>
                    <DailyBars isFR={isFR} data-id="element-7666" data-mp="4cs6c0q8tmvs" />
                  </div>
                  <div className={`${PANEL} p-3`} data-id="element-7667" data-mp="d1cfo5f6qn1">
                    <p className="font-sans text-[11px] font-semibold text-[#121A3A] mb-2" data-id="element-7668" data-mp="c9gudbtoswr">{pick(d.sectorTitle)}</p>
                    <SectorDonut isFR={isFR} data-id="element-7669" data-mp="e5qw3lxh7jgz" />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="h-[14px] mx-[-3%] rounded-b-[14px] bg-[#C9CBD3] shadow-[0_24px_40px_-24px_rgba(20,20,60,0.6)]" aria-hidden="true" data-id="element-7670" data-mp="3hl51zgywu1jr" />
    </div>;
}